<template>
  <div class="w50 pd-rt-7 chart-box">
    <div class="time-tracking-report-chart">
      <div class="chart-top">
        <div class="chart-top-lt">
          <h2>
            {{ constants.TIME_TRACKING_REPORT_TEXT }}
          </h2>
        </div>
        <div class="chart-top-rt">
          <div class="chart-select pd-rt-7">
            <select class="v1-selectbox" v-model="selectedProject" @change="fetchSprintData">
              <option value="">Select Project</option>
              <option v-for="project in projects" :key="project.key" :value="project.key">
                {{ project.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="chart-btm">
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner">Loading sprint data...</div>
        </div>
        <div class="chart-container" v-else>
          <canvas ref="chartCanvas" width="600" height="400"></canvas>
        </div>
        <div v-if="chartError" class="chart-error">
          <p>{{ chartError }}</p>
          <button @click="retryChart" class="retry-chart-btn">Retry Chart</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import constants from "@/utils/en-US-Employee.json";
import { Chart } from 'chart.js/auto';
import { API_BASE_URL } from "@/config/api.config";
import axios from "axios";

interface Project {
  key: string;
  name: string;
  id: string;
}

interface SprintData {
  name: string;
  estimated: number;
  logged: number;
  id?: string;
  state?: string;
  startDate?: string;
  endDate?: string;
}

export default defineComponent({
  name: "Chart1",
  data() {
    return {
      constants,
      selectedProject: "",
      projects: [] as Project[],
      sprintData: [] as SprintData[],
      chart: null as any,
      chartError: "",
      username: "", // This should be set from user context
      isLoading: false,
      renderRetryCount: 0,
      maxRetries: 3,
    };
  },
  async mounted() {
    // Get username from localStorage or user context
    this.username = localStorage.getItem('jira_user_username') || 'demo_user';
    await this.fetchProjects();
    
    // Render empty chart by default
    this.$nextTick(() => {
      setTimeout(() => {
        this.renderChart();
      }, 100);
    });
  },
  methods: {
    async fetchProjects() {
      try {
        const response = await axios.get(`${API_BASE_URL}/jira/projects/${this.username}/`);
        if (response.status === 200) {
          const data = response.data;
          this.projects = data.projects || [];
          console.log('Projects fetched:', this.projects);
        } else {
          console.error('Failed to fetch projects:', response.status);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    },
    async fetchSprintData() {
      if (!this.selectedProject) {
        this.sprintData = [];
        this.chartError = "Please select a project to view sprint data";
        return;
      }
      
      this.isLoading = true;
      this.chartError = "";
      
      try {
        console.log('Fetching sprint data for project:', this.selectedProject);
        // Fetch sprint data for the selected project
        const response = await axios.get(`${API_BASE_URL}/jira/sprint-data/${this.username}/${this.selectedProject}/`);
        if (response.status === 200) {
          const data = response.data;
          this.sprintData = data.sprints || [];
          console.log('Sprint data received:', this.sprintData);
          
          // Always render chart, even with empty data
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderChart();
            }, 100);
          });
          
          if (this.sprintData.length === 0) {
            this.chartError = "No sprint data available for this project";
          }
        } else {
          this.chartError = `Failed to fetch sprint data for project: ${this.selectedProject}. Status: ${response.status}`;
          console.error('Error fetching sprint data:', response.status);
        }
      } catch (error) {
        console.error('Error fetching sprint data:', error);
        this.chartError = "Failed to fetch sprint data. Please try again later.";
        // Set empty data and render empty chart
        this.sprintData = [];
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderChart();
          }, 100);
        });
      } finally {
        this.isLoading = false;
      }
    },
    renderChart() {
      console.log('Attempting to render chart...');
      
      // Clear any existing chart
      if (this.chart) {
        try {
          this.chart.destroy();
          this.chart = null;
        } catch (error) {
          console.error('Error destroying existing chart:', error);
        }
      }

      // Wait for next tick to ensure DOM is ready
      this.$nextTick(() => {
        const ctx = this.$refs.chartCanvas as HTMLCanvasElement;
        if (!ctx) {
          console.error('Canvas element not found, retrying...');
          // Retry after a short delay
          setTimeout(() => {
            this.renderChart();
          }, 200);
          return;
        }

        // Check if canvas context is available
        try {
          const context = ctx.getContext('2d');
          if (!context) {
            console.error('Canvas context not available');
            this.chartError = "Canvas context not available";
            return;
          }
        } catch (error) {
          console.error('Error getting canvas context:', error);
          this.chartError = "Canvas context error";
          return;
        }

        try {
          console.log('Creating new chart with data:', this.sprintData);
          
          // Prepare chart data - use empty data if no sprints available
          const labels = this.sprintData.length > 0 ? this.sprintData.map(sprint => sprint.name) : ['No Data'];
          const estimatedData = this.sprintData.length > 0 ? this.sprintData.map(sprint => sprint.estimated) : [0];
          const loggedData = this.sprintData.length > 0 ? this.sprintData.map(sprint => sprint.logged) : [0];
          
          this.chart = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: labels,
              datasets: [
                {
                  label: 'Estimated Time (hours)',
                  data: estimatedData,
                  backgroundColor: 'rgba(54, 162, 235, 0.8)',
                  borderColor: 'rgba(54, 162, 235, 1)',
                  borderWidth: 2,
                  borderRadius: 5,
                  borderSkipped: false,
                  type: 'bar'
                },
                {
                  label: 'Logged Time (hours)',
                  data: loggedData,
                  backgroundColor: 'rgba(75, 192, 75, 0.8)',
                  borderColor: 'rgba(75, 192, 75, 1)',
                  borderWidth: 2,
                  borderRadius: 5,
                  borderSkipped: false,
                  type: 'bar'
                }
              ]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Time (hours)'
                  }
                },
                x: {
                  title: {
                    display: true,
                    text: 'Sprints'
                  }
                }
              },
              plugins: {
                title: {
                  display: true,
                  text: this.sprintData.length > 0 
                    ? `Time Tracking Report - ${this.selectedProject || 'Sample Data'}`
                    : 'Time Tracking Report - No Data Available',
                  font: {
                    size: 16
                  }
                },
                legend: {
                  display: true,
                  position: 'top'
                },
                tooltip: {
                  callbacks: {
                    label: function(context: any) {
                      return context.dataset.label + ': ' + context.parsed.y + ' hours';
                    }
                  }
                }
              }
            }
          });
          console.log('Chart rendered successfully');
          this.chartError = ""; // Clear any previous errors
        } catch (error) {
          this.chartError = "Failed to render chart. Please try again later.";
          console.error('Error rendering chart:', error);
        }
      });
    },
    retryChart() {
      this.chartError = "";
      this.renderRetryCount = 0;
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderChart();
        }, 300);
      });
    }
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  }
});
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 450px;
  position: relative;
  /* border: 1px solid #ddd; */
  border-radius: 8px;
  padding: 20px;
  background-color: #fff;
}

.chart-container canvas {
  max-width: 100%;
  height: auto;
  display: block;
}

.chart-error {
  color: red;
  font-weight: bold;
  padding: 10px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin-top: 10px;
  text-align: center;
}

.retry-chart-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  font-size: 14px;
}

.retry-chart-btn:hover {
  background: #0056b3;
}

.no-project-selected {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: #666;
  font-style: italic;
  font-size: 16px;
  text-align: center;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #007bff;
  font-size: 16px;
}

.loading-spinner::before {
  content: '';
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
